import Cookies from 'js-cookie'

import { COOKIE_KEYS } from '@/lib/configs/auth-config'

/**
 * Reads the current user id directly from the auth cookie, synchronously — no React
 * state involved. `dashboard-*` localStorage keys (layouts, focused page, watchlists,
 * linking groups, etc.) are identity-sensitive: on a shared browser, a stale value left
 * behind by a previous user must never be readable by — or worse, silently rewritten
 * under — the next user who logs in. Namespacing the key itself by user id closes that
 * hole structurally, instead of relying on every login path remembering to clear storage
 * (atomWithStorage's `getOnInit: true` reads synchronously at import time, before any
 * React effect could detect a user switch and react to it).
 */
export function getCurrentUserId(): string {
  if (typeof window === 'undefined') return 'ssr'
  try {
    // js-cookie already URL-decodes on read (it encoded on the matching `Cookies.set`
    // in atomWithCookie) — no extra decodeURIComponent needed, that would double-decode.
    const raw = Cookies.get(COOKIE_KEYS.AUTH_STATE)
    if (!raw) return 'guest'
    const parsed = JSON.parse(raw) as { user?: { userId?: string } | null }
    return parsed.user?.userId || 'guest'
  } catch {
    return 'guest'
  }
}

// Only identity-sensitive dashboard keys need per-user isolation — device-level UI prefs
// (sound toggle, RFQ dock position, option-table column config) are intentionally left
// shared across logins on the same browser.
//
// Exported so callers that bypass the storage adapter and touch `localStorage` directly
// (e.g. resetAllStorage's explicit removeItem calls) compute the SAME on-disk key —
// otherwise removing the un-namespaced key would be a no-op against the namespaced one
// this adapter actually wrote.
//
// `userId` can be passed explicitly for cleanup paths that already know which user they're
// clearing (e.g. logout, which can fire this before/after clearing the auth cookie without
// the result changing) — falls back to reading the live cookie when omitted.
export function namespacedKey(key: string, userId?: string): string {
  return key.startsWith('dashboard-') ? `${key}:${userId ?? getCurrentUserId()}` : key
}

// Debounce window for persisting a single key to disk. `atomWithStorage` calls
// `setItem` synchronously inside every `set()` — a click handler that fires several
// setState calls in a row (e.g. positions-tab-contents' handleSymbolClick writing
// selectedOptionTicker, selectedOptionExpiry, watchList, ...) would otherwise
// synchronously JSON.stringify + localStorage.setItem the *entire* per-page linking
// group map (all 7 color groups' chart options, position filters, etc.) once per
// field, blocking the click's render commit each time. Coalescing same-key writes
// into one deferred flush keeps in-memory atom reads correct immediately while
// moving the disk IO off the interaction's critical path.
//
// The pending value is held as the RAW object, not a string: jotai's own
// `createJSONStorage` stringifies inside its `setItem` — i.e. before this adapter is
// ever reached — so debouncing alone still left a full `JSON.stringify` of the entire
// per-page linking-group map on every single `set()`. Serializing at flush time
// instead moves that cost off the interaction too, and coalesced writes now stringify
// once rather than once per superseded value. Safe because every writer here produces
// a new immutable object (jotai convention) — nothing mutates a value after handing
// it over.
const PERSIST_DEBOUNCE_MS = 150
type PendingWrite = {
  value: unknown
  // Explicit marker that `value` is a raw object still awaiting serialization. An entry
  // without it came from a build of this module that stored pre-serialized strings, so
  // flushing must NOT stringify again — see decodeStoredValue for how that mismatch
  // corrupted keys during a dev Fast Refresh across this change.
  serialize?: true
  timer: ReturnType<typeof setTimeout>
}
const pendingWrites = new Map<string, PendingWrite>()

function flushPendingWrite(base: Storage, key: string) {
  const pending = pendingWrites.get(key)
  if (!pending) return
  pendingWrites.delete(key)
  try {
    base.setItem(key, pending.serialize ? JSON.stringify(pending.value) : String(pending.value))
  } catch {
    // Storage full/blocked (e.g. private browsing) — drop silently, same as before.
  }
}

/**
 * Cancels any not-yet-flushed debounced writes without persisting them. Callers that
 * remove keys directly via `localStorage.removeItem` (bypassing this adapter — see
 * `resetAllStorage`) must call this first, or a pending write could flush moments
 * later and silently resurrect data the caller just deleted.
 */
export function clearPendingStorageWrites() {
  for (const { timer } of pendingWrites.values()) clearTimeout(timer)
  pendingWrites.clear()
}

function getBaseStorage(): Storage | null {
  if (
    typeof window === 'undefined' ||
    !window.localStorage ||
    typeof window.localStorage.getItem !== 'function'
  ) {
    return null
  }
  return window.localStorage
}

// Keyed on the window we attached to, not a plain boolean: this module is a singleton
// shared across a test run, where each file installs its own `window` double. A
// once-only flag would leave every window after the first without flush listeners —
// the previous `createJSONStorage` wrapper re-ran its factory on every access, so it
// re-attached implicitly. Re-attaching per window preserves that while still not
// stacking duplicate listeners on a single window.
let listenersAttachedTo: unknown = null
function attachFlushListeners(base: Storage) {
  if (typeof window === 'undefined' || listenersAttachedTo === window) return
  listenersAttachedTo = window
  const flushAllPendingWrites = () => {
    for (const key of Array.from(pendingWrites.keys())) flushPendingWrite(base, key)
  }
  window.addEventListener('pagehide', flushAllPendingWrites)
  window.addEventListener('beforeunload', flushAllPendingWrites)
}

// Mirrors createJSONStorage's own parse memo: repeated reads of an unchanged key
// return the identical object reference, so two atomFamily instances sharing a key
// don't each get their own deep copy.
const parseCache = new Map<string, { raw: string; value: unknown }>()

/**
 * Unwraps a value that was persisted one JSON layer too deep.
 *
 * `pendingWrites` is module state with live ESM bindings, so a dev Fast Refresh of this
 * file could leave a not-yet-refreshed consumer holding the previous `createJSONStorage`
 * wrapper — which serializes *before* reaching this adapter — while `flushPendingWrite`
 * had already become the version that serializes at flush time. That window wrote a few
 * keys double-encoded, and reading one back yields a string where an array/object is
 * expected (a string's truthy `.length` sails past emptiness guards and then blows up on
 * `.map`). Rather than leave those keys permanently poisoned, recover them on read.
 *
 * Deliberately narrow: only when the atom's own `initialValue` is an object/array (so a
 * string is definitely wrong) AND the decoded string actually looks like encoded JSON.
 * Atoms that genuinely store strings are never touched.
 */
function decodeStoredValue(value: unknown, initialValue: unknown): unknown {
  if (
    typeof value !== 'string' ||
    initialValue === null ||
    typeof initialValue !== 'object' ||
    !(value.startsWith('[') || value.startsWith('{'))
  ) {
    return value
  }
  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

/**
 * Safe localStorage wrapper that handles SSR and broken localStorage environments.
 * Extracted as a standalone utility to avoid circular dependencies between state modules.
 *
 * Implements jotai's `SyncStorage` directly rather than wrapping `createJSONStorage`,
 * so serialization is deferred to the debounced flush (see `flushPendingWrite`) instead
 * of running synchronously inside every atom `set()`.
 *
 * No `subscribe` is provided — and none is lost: `createJSONStorage` only wires up its
 * cross-tab `storage` listener when the underlying object is `instanceof window.Storage`,
 * which the previous hand-rolled wrapper object never was, so cross-tab sync was already
 * inert here.
 */
export const safeStorage = {
  // Read-your-own-write: a getItem racing a not-yet-flushed setItem (e.g. another
  // atomFamily instance for the same key initializing mid-debounce) must see the
  // latest in-flight value, not the stale on-disk one.
  getItem: (key: string, initialValue: any) => {
    const nsKey = namespacedKey(key)
    const pending = pendingWrites.get(nsKey)
    // An unmarked entry is a pre-serialized string from an older build of this module
    // (see PendingWrite.serialize) — decode it rather than handing back the raw JSON.
    if (pending) {
      if (pending.serialize) return pending.value
      try {
        return decodeStoredValue(JSON.parse(String(pending.value)), initialValue)
      } catch {
        return initialValue
      }
    }

    const base = getBaseStorage()
    if (!base) return initialValue
    attachFlushListeners(base)

    const raw = base.getItem(nsKey)
    if (raw === null) return initialValue

    const cached = parseCache.get(nsKey)
    if (cached && cached.raw === raw) return cached.value
    try {
      const value = decodeStoredValue(JSON.parse(raw), initialValue)
      parseCache.set(nsKey, { raw, value })
      return value
    } catch {
      return initialValue
    }
  },
  setItem: (key: string, value: any) => {
    const base = getBaseStorage()
    if (!base) return
    attachFlushListeners(base)
    const nsKey = namespacedKey(key)
    const existing = pendingWrites.get(nsKey)
    if (existing) clearTimeout(existing.timer)
    const timer = setTimeout(() => flushPendingWrite(base, nsKey), PERSIST_DEBOUNCE_MS)
    pendingWrites.set(nsKey, { value, serialize: true, timer })
  },
  removeItem: (key: string) => {
    const nsKey = namespacedKey(key)
    const existing = pendingWrites.get(nsKey)
    if (existing) {
      clearTimeout(existing.timer)
      pendingWrites.delete(nsKey)
    }
    parseCache.delete(nsKey)
    getBaseStorage()?.removeItem(nsKey)
  },
}
